// accountBalanceStatusController.js - Manages state and API interactions for account balance comments/status.

class AccountBalanceStatusController {
  localStorageKey = 'lux_balanceDateRange'

  constructor(id) {
    if (!id || typeof id !== 'number' || isNaN(id)) {
      throw new Error('Invalid ID provided to AccountBalanceStatusController')
    }

    this.id = id // The ID used for initialization (could be postingItemId or exchange_plan_id)
    this.postingItemId = id // Store postingItemId specifically
    this.currentData = null // Holds the latest { status, comment, commentUpdatedAt, commentUpdatedBy, postingDate, ... }
    this._listeners = {} // For event emitter pattern

    // State for standing order logic
    this.isStandingOrder = false
    this.postingDate = null
    this.lastAttemptedComment = null

    console.log(`Controller created for: ${this.id}`)
  }

  // --- Event Emitter Methods ---
  on(eventName, listener) {
    if (!this._listeners[eventName]) {
      this._listeners[eventName] = []
    }
    this._listeners[eventName].push(listener)
    console.log(`Listener added for event: ${eventName}`)
  }

  off(eventName, listenerToRemove) {
    if (!this._listeners[eventName]) {
      return
    }
    this._listeners[eventName] = this._listeners[eventName].filter(
      (listener) => listener !== listenerToRemove
    )
    console.log(`Listener removed for event: ${eventName}`)
  }

  emit(eventName, data) {
    console.log(`Emitting event: ${eventName}`, data)
    if (!this._listeners[eventName]) {
      return
    }
    this._listeners[eventName].forEach((listener) => {
      try {
        listener(data)
      } catch (error) {
        console.error(`Error in listener for event ${eventName}:`, error)
      }
    })
  }

  // --- Data Fetching and State Management ---
  getCurrentData() {
    return this.currentData
  }

  async fetchInitialData() {
    console.log(`Controller: Fetching initial data for: ${this.id}`)

    try {
      const response = await Lux_ApiClient_get(
        `/api/account-balances?postingItemIds=${this.id}`
      )
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `API error: ${response.status}`)
      }

      const dataMap = await response.json()
      const initialBalanceData = dataMap[this.id] || {
        status: 'open',
        comment: null,
        commentUpdatedAt: null,
        commentUpdatedBy: null,
        postingDate: null,
      }
      this.currentData = initialBalanceData

      console.log('Controller: Initial fetch successful', this.currentData)
      this.emit('update', this.currentData) // Also emit generic update
    } catch (error) {
      console.error(
        `Controller: Error fetching initial data ${this.id}:`,
        error
      )
      this.currentData = { status: 'error', comment: null } // Indicate error state
    }
  }

  // --- Actions ---

  /**
   * Initiates the comment saving process. May trigger UI prompts.
   * @param {string|null} newComment
   */
  async saveComment(newComment, mode = 'single order') {
    this.lastAttemptedComment =
      typeof newComment === 'string' && newComment.trim() !== ''
        ? newComment.trim()
        : null
    console.log(
      `Controller: Initiating saveComment for postingItemId: ${this.postingItemId}. Standing Order: ${this.isStandingOrder}`
    )

    if (!this.postingItemId) {
      console.error('Controller: Cannot save comment without postingItemId.')
      return
    }

    this._performSave(
      this.lastAttemptedComment,
      'feedback needed from administrator',
      mode
    )
  }

  /**
   * Internal: Performs the actual API call to save the comment.
   * @param {string|null} comment
   * @param {'single order'|'standing order'} mode
   */
  async _performSave(comment, status, mode) {
    const dateRangeString = localStorage.getItem(this.localStorageKey)
    let startDate = null
    let endDate = null
    if (dateRangeString) {
      const dateRange = JSON.parse(dateRangeString)
      startDate = dateRange.startDate
      endDate = dateRange.endDate
    }

    const userName =
      localStorage.getItem('userName') ||
      JSON.parse(localStorage.getItem('auth'))?.email ||
      'unknown'

    try {
      const payload = {
        postingItemId: this.postingItemId,
        ...(comment ? { comment } : {}),
        status,
        mode,
        userName,
        startDate,
        endDate,
      }

      const response = await Lux_ApiClient_saveAccountBalanceComment(payload)

      if (response.ok) {
        // Status 200-299
        const responseData = await response.json()
        if (!response.ok) {
          throw new Error(responseData.error || `API Error: ${response.status}`)
        }

        for (const item of responseData) {
          const key = item.postingItemId
          const postingItemId = parseInt(key, 10)
          if (!isNaN(postingItemId)) {
            window.Lux_postingItemDataMap[postingItemId] = item
          }
        }
        console.log('Controller: Save API call successful', responseData)
        this.emit('update', responseData)
        this.emit('saveSuccess', {
          responseData: responseData,
        })
      } else {
        // Other errors
        const errorData = await response
          .json()
          .catch(() => ({ error: `API Error: ${response.status}` }))
        throw new Error(errorData.error || `API Error: ${response.status}`)
      }
    } catch (error) {
      console.error(
        `Controller: Error performing save for postingItemId ${this.postingItemId}:`,
        error
      )
    }
  }

  async approveBalance(mode) {
    // This logic approves either one balance or all balances of a given invoice for the current period
    console.log(
      `Controller: Approving all balances for postingItemId: ${this.postingItemId}`
    )
    if (this.currentData?.status === 'approved') {
      console.warn('Controller: Balance already approved.')
      return // Avoid redundant API call
    }

    try {
      this._performSave(null, 'approved', mode)
      this.emit('approveSuccess', this.currentData)
    } catch (error) {
      console.error(
        `Controller: Error approving balance for postingItemId ${this.postingItemId}:`,
        error
      )
    }
  }
}
